import React, { useState, useEffect } from 'react';
import { FaTimes, FaProjectDiagram, FaBuilding, FaTasks, FaMapMarkerAlt, FaUser, FaFileAlt, FaUsers } from 'react-icons/fa';
import { SearchableSelect, SelectWithSearch } from '../../utils/select-with-search';
import { CloseModal } from '../sites/closemodal';

interface Activity {
  id: number;
  activity_name: string;
  activity_short_name: string;
}

interface Client {
  id: number;
  client_full_name: string;
  client_short_name: string;
}

interface StateCircle {
  id: number;
  state_name: string;
}

interface ClientProject {
  id: number;
  project_name: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
}

interface UsersByRole {
  "Technical Associate": User[];
  "MIS": User[];
  "Team Coordinator": User[];
}

interface UserProject {
  id: number;
  user_id: number;
  project_id: number;
}

interface ProjectFormData {
  activity_id: number;
  client_id: number;
  state_circle_id: number;
  client_project_id?: number;
  project_name: string;
  project_short_name: string;
  project_description?: string;
  user_ids?: number[];
  status?: string;
  budget?: number;
  nearby_site_radius_km?: number;
  // INC: may engineers check in to a visit without verification approval?
  inc_checkin_without_verification?: boolean;
}

interface AddProjectOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ProjectFormData) => void;
  activities: Activity[];
  clients: Client[];
  stateCircles: StateCircle[];
  users: User[];
  clientProjects: ClientProject[];
  onClientChange: (clientId: number) => void;
  loading?: boolean;
}

interface Option {
  value: string | number;
  label: string;
  data?: any;
}

export const AddProjectOffcanvas: React.FC<AddProjectOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  activities,
  clients,
  stateCircles,
  users,
  clientProjects,
  onClientChange,
  loading = false
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    activity_id: 0,
    client_id: 0,
    state_circle_id: 0,
    client_project_id: undefined,
    project_name: '',
    project_short_name: '',
    project_description: '',
    user_ids: [],
    status: undefined,
    budget: undefined,
    nearby_site_radius_km: 30,
    inc_checkin_without_verification: true
  });

  const [errors, setErrors] = useState<Partial<ProjectFormData>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Select states
  const [selectedClient, setSelectedClient] = useState<Option | null>(null);
  const [selectedStateCircle, setSelectedStateCircle] = useState<any | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<Option | null>(null);
  const [selectedClientProject, setSelectedClientProject] = useState<Option | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Option[] | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<Option | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const statusOptions: Option[] = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'SUSPENDED', label: 'Suspended' },
  ];

  // Convert props to options with null checks
  const activityOptions: Option[] = activities?.map(activity => ({
    value: activity.id,
    label: activity.activity_name
  })) || [];

  const clientOptions: Option[] = clients?.map(client => ({
    value: client.id,
    label: client.client_full_name
  })) || [];

  const clientProjectOptions: Option[] = clientProjects?.map(project => ({
    value: project.id,
    label: project.project_name
  })) || [];

  const userOptions: Option[] = [
    ...(users?.map(user => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`
    })) || []),
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        activity_id: 0,
        client_id: 0,
        state_circle_id: 0,
        client_project_id: undefined,
        project_name: '',
        project_short_name: '',
        project_description: '',
        user_ids: [],
        status: undefined,
        budget: undefined,
        nearby_site_radius_km: 30,
        inc_checkin_without_verification: true
      });
      setErrors({});
      setTouched({});
      setSelectedClient(null);
      setSelectedStateCircle(null);
      setSelectedActivity(null);
      setSelectedClientProject(null);
      setSelectedUsers(null);
      setSelectedStatus(null);
    } else {
      setShowCloseModal(false);
    }
  }, [isOpen]);

  // Validation
  const validateField = (name: string, value: any) => {
    switch (name) {
      case 'nearby_site_radius_km':
        if (value !== undefined && value !== null && value !== '' && (isNaN(Number(value)) || Number(value) <= 0 || Number(value) > 5000)) {
          return 'Radius must be greater than 0 and at most 5000 km';
        }
        break;
      case 'project_name':
        if (!value || !String(value).trim()) return 'Project name is required';
        if (String(value).trim().length < 3) return 'Project name must be at least 3 characters';
        if (String(value).trim().length > 200) return 'Project name must be less than 200 characters';
        break;
      case 'project_short_name':
        if (!value || !String(value).trim()) return 'Short name is required';
        if (String(value).trim().length < 2) return 'Short name must be at least 2 characters';
        if (String(value).trim().length > 50) return 'Short name must be less than 50 characters';
        break;
      case 'activity_id':
        if (!value || value === 0) return 'Activity is required';
        break;
      case 'client_id':
        if (!value || value === 0) return 'Client is required';
        break;
      case 'state_circle_id':
        if (!value || value === 0) return 'State/Circle is required';
        break;
      case 'project_description':
        if (value && String(value).trim().length > 1000) return 'Description must be less than 1000 characters';
        break;
    }
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    let processedValue: any = value;

    if (type === 'number') {
      processedValue = value === '' ? undefined : Number(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name as keyof ProjectFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Validate field if touched
    if (touched[name]) {
      const error = validateField(name, processedValue);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, formData[name as keyof ProjectFormData]);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleClientChange = (option: Option | null) => {
    setSelectedClient(option);
    setFormData(prev => ({
      ...prev,
      client_id: option ? Number(option.value) : 0,
      client_project_id: undefined
    }));
    setSelectedClientProject(null);

    if (option) {
      onClientChange(Number(option.value));
    }

    if (errors.client_id) {
      setErrors(prev => ({
        ...prev,
        client_id: undefined
      }));
    }
  };

  const handleStateCircleChange = (option: any | null) => {
    setSelectedStateCircle(option);
    setFormData(prev => ({
      ...prev,
      state_circle_id: option ? Number(option.value) : 0
    }));

    if (errors.state_circle_id) {
      setErrors(prev => ({
        ...prev,
        state_circle_id: undefined
      }));
    }
  };

  const handleActivityChange = (option: Option | null) => {
    setSelectedActivity(option);
    setFormData(prev => ({
      ...prev,
      activity_id: option ? Number(option.value) : 0
    }));

    if (errors.activity_id) {
      setErrors(prev => ({
        ...prev,
        activity_id: undefined
      }));
    }
  };

  const handleClientProjectChange = (option: Option | null) => {
    setSelectedClientProject(option);
    setFormData(prev => ({
      ...prev,
      client_project_id: option ? Number(option.value) : undefined
    }));
  };

  const handleUsersChange = (options: any) => {
    setSelectedUsers(options);
    setFormData(prev => ({
      ...prev,
      user_ids: options ? options.map((o: any) => Number(o.value)) : []
    }));
  };

  const handleStatusChange = (option: Option | null) => {
    setSelectedStatus(option);
    setFormData(prev => ({
      ...prev,
      status: option ? String(option.value) : undefined
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    // Validate required fields
    const fieldsToValidate = [
      'activity_id', 'client_id', 'state_circle_id', 'project_name',
      'project_short_name', 'project_description', 'nearby_site_radius_km'];

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field as keyof ProjectFormData]);
      if (error) {
        newErrors[field as keyof ProjectFormData] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched({
      activity_id: true,
      client_id: true,
      state_circle_id: true,
      project_name: true,
      project_short_name: true,
      project_description: true,
    });

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const submitData: ProjectFormData = {
      activity_id: formData.activity_id,
      client_id: formData.client_id,
      state_circle_id: formData.state_circle_id,
      client_project_id: formData.client_project_id,
      project_name: formData.project_name.trim(),
      project_short_name: formData.project_short_name.trim(),
      project_description: formData.project_description?.trim() || undefined,
      user_ids: formData.user_ids,
      status: formData.status,
      budget: formData.budget,
      nearby_site_radius_km: formData.nearby_site_radius_km,
      inc_checkin_without_verification: formData.inc_checkin_without_verification !== false,
    };

    onSubmit(submitData);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        setShowCloseModal(true);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  const requestClose = () => {
    if (!loading) {
      setShowCloseModal(true);
    }
  };

  const confirmClose = () => {
    if (!loading) {
      setShowCloseModal(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={requestClose} />

      {/* Offcanvas */}
      <div className="overflow-y-auto fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Create New Project</h2>
            <p className="text-sm text-gray-600 mt-1">Add a new ESPL project to the system</p>
          </div>
          <button
            onClick={requestClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-32">
          <div className="p-6">
            <div onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Project Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <FaProjectDiagram className="w-5 h-5 text-blue-600" />
                  Project Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Project Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter full project name"
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.project_name && touched.project_name
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      disabled={loading}
                    />
                    {errors.project_name && touched.project_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.project_name}</p>
                    )}
                  </div>

                  {/* Project Short Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="project_short_name"
                      value={formData.project_short_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter project short name"
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.project_short_name && touched.project_short_name
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      disabled={loading}
                    />
                    {errors.project_short_name && touched.project_short_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.project_short_name}</p>
                    )}
                  </div>

                  {/* Project Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Description
                    </label>
                    <textarea
                      name="project_description"
                      value={formData.project_description || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter project description"
                      rows={3}
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none ${
                        errors.project_description && touched.project_description
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      disabled={loading}
                    />
                    {errors.project_description && touched.project_description && (
                      <p className="mt-1 text-sm text-red-600">{errors.project_description}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Client & Activity Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <FaBuilding className="w-5 h-5 text-blue-600" />
                  Client & Activity Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Client */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={selectedClient}
                      onChange={handleClientChange}
                      options={clientOptions}
                      placeholder="Select client"
                      disabled={loading}
                    />
                    {errors.client_id && touched.client_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>
                    )}
                  </div>

                  {/* Activity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Activity <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={selectedActivity}
                      onChange={handleActivityChange}
                      options={activityOptions}
                      placeholder="Select activity"
                      disabled={loading}
                    />
                    {errors.activity_id && touched.activity_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.activity_id}</p>
                    )}
                  </div>

                  {/* State/Circle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State/Circle <span className="text-red-500">*</span>
                    </label>
                    <SelectWithSearch
                      value={selectedStateCircle}
                      onChange={handleStateCircleChange}
                      placeholder="Select state"
                      apiEndpoint="/state-circle/list"
                      dataKey="states"
                      labelKey="state_name"
                      displayFormat={(item) => item.state_name}
                    />
                    {errors.state_circle_id && touched.state_circle_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.state_circle_id}</p>
                    )}
                  </div>

                  {/* Client Project */}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Project (Optional)
                    </label>
                    <SearchableSelect
                      value={selectedClientProject}
                      onChange={handleClientProjectChange}
                      options={clientProjectOptions}
                      placeholder="Select client project"
                      disabled={loading || !formData.client_id}
                    />
                    {!formData.client_id && (
                      <p className="mt-1 text-xs text-gray-500">Select a client first to see available projects</p>
                    )}
                  </div> */}
                </div>
              </div>

              {/* Team Members */}
              <div className="bg-gray-50 border border-gray-200 rounded p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <FaUsers className="w-5 h-5 text-blue-600" />
                  Team Members (Optional)
                </h3>

                <div className="grid grid-cols-1 gap-6">
                  {/* Assign Users */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign Users
                    </label>
                    <SearchableSelect
                      value={selectedUsers}
                      onChange={handleUsersChange}
                      options={userOptions}
                      placeholder="Search and select users..."
                      disabled={loading}
                      isMulti={true}
                    />
                  </div>
                </div>
              </div>

              {/* Project Status & Budget */}
              <div className="bg-gray-50 border border-gray-200 rounded p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <FaTasks className="w-5 h-5 text-blue-600" />
                  Project Status & Budget
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <SearchableSelect
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      options={statusOptions}
                      placeholder="Select status"
                      disabled={loading}
                    />
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget
                    </label>
                    <input
                      type="number"
                      name="budget"
                      value={formData.budget || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter budget amount"
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.budget && touched.budget
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      disabled={loading}
                      step="0.01"
                      min="0"
                    />
                    {errors.budget && touched.budget && (
                      <p className="mt-1 text-sm text-red-600">{errors.budget}</p>
                    )}
                  </div>

                  {/* Nearby Sites Radius */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nearby Sites Radius (km)
                    </label>
                    <input
                      type="number"
                      name="nearby_site_radius_km"
                      value={formData.nearby_site_radius_km ?? ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 50"
                      className={`w-full px-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${(errors as any).nearby_site_radius_km && touched.nearby_site_radius_km
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                        }`}
                      disabled={loading}
                      step="0.1"
                      min="0.1"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Site engineers see this project's unassigned sites within this distance. Defaults to 30 km.
                    </p>
                    {(errors as any).nearby_site_radius_km && touched.nearby_site_radius_km && (
                      <p className="mt-1 text-sm text-red-600">{(errors as any).nearby_site_radius_km}</p>
                    )}
                  </div>

                  {/* INC: verification before visit check-in */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      INC Visit Check-in
                    </label>
                    <div className="flex items-start justify-between gap-4 border border-gray-300 rounded px-3 py-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Allow check-in without verification
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {formData.inc_checkin_without_verification !== false
                            ? "On: engineers can check in to an INC visit of this project straight away."
                            : "Off: an engineer cannot check in until the visit verification and the safety verification of that visit are both approved."}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={formData.inc_checkin_without_verification !== false}
                        aria-label="Allow INC check-in without verification"
                        disabled={loading}
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            inc_checkin_without_verification: prev.inc_checkin_without_verification === false
                          }))
                        }
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${formData.inc_checkin_without_verification !== false ? 'bg-blue-800' : 'bg-gray-300'
                          }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.inc_checkin_without_verification !== false ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-gray-50 border border-gray-200 rounded flex items-center justify-between p-4">
          <button
            type="button"
            onClick={requestClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {loading ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};